import { useState, useRef, DragEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNotifications } from "@/contexts/NotificationContext";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import DropZone from "./DropZone";
import FilePreview from "./FilePreview";
import { formatBytes } from "@/lib/utils";
import { useDevice } from "@/hooks/useDevice";

const FILE_SIZE_LIMIT = 5 * 1024 * 1024 * 1024; // 5GB

interface FileUploaderProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  uploadProgress?: number;
  isUploading?: boolean;
}

const FileUploader = ({
  onFileSelect,
  selectedFile,
  uploadProgress = 0,
  isUploading = false
}: FileUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const { shouldDisableDragDrop } = useDevice();
  const { t } = useTranslation();

  const validateFile = (file: File): boolean => {
    if (file.size > FILE_SIZE_LIMIT) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('index.validations.size_error', { size: formatBytes(FILE_SIZE_LIMIT) }),
      });
      return false;
    }

    if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('index.validations.type_error'),
      });
      return false;
    }

    return true;
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        onFileSelect(droppedFile);
      }
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        onFileSelect(selectedFile);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeFile = () => {
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      {!selectedFile ? (
        <DropZone
          onFileSelect={onFileSelect}
          isDragging={isDragging}
          onDragOver={!shouldDisableDragDrop ? (e) => {
            e.preventDefault();
            setIsDragging(true);
          } : () => { }}
          onDragLeave={!shouldDisableDragDrop ? () => setIsDragging(false) : () => { }}
          onDrop={!shouldDisableDragDrop ? handleFileDrop : () => { }}
          triggerFileInput={triggerFileInput}
        />
      ) : (
        <FilePreview
          file={selectedFile}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          onRemove={removeFile}
          onUpload={() => { }}
        />
      )}

      <input
        type="file"
        className="hidden"
        accept="audio/*,video/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.mp4,.mov,.avi,.mkv,.webm,.wmv"
        ref={fileInputRef}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileUploader;
